import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useMyCapabilities } from '../utils/metadata/capabilities';
import { invalidateAndRefreshAllQueriesForTable } from '../utils/metadata';
import { supabaseClient } from '../utils/supabase';
import { handleOperationError } from '../utils/supabase-operations';
import { queryPolicies } from '../utils/queryPolicies';
import { DEFAULT_CLOSE_REQUEST_REASON } from './DealCloseRequest/RequestCloseDialog';
import { activePrimaryOverrideQueryOptions } from '../utils/destinationQueries/merchantPanel';
import './PrimaryApprovalActions.css';

export function useActivePrimaryOverride(merchantId: number) {
  return useQuery({
    ...activePrimaryOverrideQueryOptions(merchantId),
    enabled: merchantId > 0,
  });
}

async function refreshPrimaryState(queryClient: ReturnType<typeof useQueryClient>, merchantId: number) {
  await queryClient.invalidateQueries({ queryKey: ['primary-approval-override', merchantId] });
  await Promise.all([
    invalidateAndRefreshAllQueriesForTable('merchant'),
    invalidateAndRefreshAllQueriesForTable('submission'),
  ]);
}

export function SubmissionPrimaryApprovalActions({
  submissionId,
  merchantId,
  status,
}: {
  submissionId: number;
  merchantId: number;
  status: string | null;
}) {
  const queryClient = useQueryClient();
  const { hasCapability } = useMyCapabilities();
  const { data: activeOverride } = useActivePrimaryOverride(merchantId);
  const { data: dealCycle, isLoading: cycleLoading } = useQuery({
    queryKey: ['submission-deal-cycle', submissionId],
    queryFn: async () => {
      const { data, error } = await (supabaseClient.rpc as any)('get_submission_deal_cycle', {
        p_submission: submissionId,
      });
      if (error) throw error;
      return data as number | null;
    },
    enabled: submissionId > 0,
    ...queryPolicies.recordDetail,
    refetchOnReconnect: true,
  });
  const [showCloseForm, setShowCloseForm] = useState(false);
  // Whole-market close is the default (REG-654); openCloseForm falls back to
  // the single-submission scope when this submission has no deal cycle.
  const [scope, setScope] = useState<'submission' | 'current_cycle'>('current_cycle');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const canManageOverride = hasCapability('manage_primary_approval_override');
  const canRequestClose = hasCapability('request_deal_close');
  const eligible = ['Approved', 'Contracts In', 'Contracts Out'].includes(status ?? '');
  const isManualPrimary = activeOverride?.submission === submissionId;

  const makePrimary = useCallback(async () => {
    setBusy(true);
    try {
      const { error } = await (supabaseClient.rpc as any)('set_primary_approval_override', {
        p_submission: submissionId,
        p_reason: null,
      });
      if (error) throw error;
      await refreshPrimaryState(queryClient, merchantId);
      toast.success('Manual Primary set');
    } catch (error) {
      toast.error(handleOperationError(error, 'Failed to set Primary').userMessage);
    } finally {
      setBusy(false);
    }
  }, [merchantId, queryClient, submissionId]);

  const removePrimary = useCallback(async () => {
    setBusy(true);
    try {
      const { error } = await (supabaseClient.rpc as any)('clear_primary_approval_override', {
        p_merchant: merchantId,
        p_reason: 'Removed from submission interface',
      });
      if (error) throw error;
      await refreshPrimaryState(queryClient, merchantId);
      toast.success('Manual Primary removed; automatic Primary restored');
    } catch (error) {
      toast.error(handleOperationError(error, 'Failed to remove Primary override').userMessage);
    } finally {
      setBusy(false);
    }
  }, [merchantId, queryClient]);

  const submitCloseRequest = useCallback(async () => {
    setBusy(true);
    try {
      const { error } = await (supabaseClient.rpc as any)('submit_deal_close_request', {
        p_submission: submissionId,
        p_scope: scope,
        p_reason: reason.trim() || DEFAULT_CLOSE_REQUEST_REASON,
      });
      if (error) throw error;
      setReason('');
      setShowCloseForm(false);
      toast.success(scope === 'submission' ? 'Submission close requested' : 'Market close requested');
    } catch (error) {
      toast.error(handleOperationError(error, 'Failed to submit close request').userMessage);
    } finally {
      setBusy(false);
    }
  }, [reason, scope, submissionId]);

  if (!canManageOverride && !canRequestClose) return null;

  return (
    <div className="primary-actions">
      <div className="primary-actions-buttons">
        {canManageOverride && eligible && !isManualPrimary && (
          <button type="button" className="sky-btn sky-btn-ghost" disabled={busy} onClick={() => void makePrimary()}>
            Make Primary
          </button>
        )}
        {canManageOverride && isManualPrimary && (
          <button type="button" className="sky-btn sky-btn-ghost" disabled={busy} onClick={() => void removePrimary()}>
            Remove Primary Override
          </button>
        )}
        {canRequestClose && (
          // Same yellow close-request flag as the Approvals grid (REG-654),
          // so the action reads identically everywhere.
          <button
            type="button"
            className="primary-close-flag"
            title="Request Close"
            aria-label="Request Close"
            disabled={busy}
            onClick={() => {
              setScope(!cycleLoading && dealCycle == null ? 'submission' : 'current_cycle');
              setShowCloseForm((value) => !value);
            }}
          >
            <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="9" />
              <line x1="12" y1="7.2" x2="12" y2="13.2" />
              <circle cx="12" cy="16.6" r="0.4" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>

      {showCloseForm && (
        <div className="primary-actions-form">
          <label>
            Close
            <select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}>
              <option value="current_cycle" disabled={cycleLoading || dealCycle == null}>All Submissions</option>
              <option value="submission">This Submission</option>
            </select>
          </label>
          <label>
            Reason (optional)
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={2}
              maxLength={1000}
              placeholder={DEFAULT_CLOSE_REQUEST_REASON}
            />
          </label>
          <div className="primary-actions-form-buttons">
            <button type="button" className="sky-btn sky-btn-ghost" disabled={busy} onClick={() => setShowCloseForm(false)}>Cancel</button>
            <button type="button" className="sky-btn sky-btn-primary" disabled={busy} onClick={() => void submitCloseRequest()}>
              {busy ? 'Submitting…' : 'Submit to Admin'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MerchantPrimaryOverrideControl({ merchantId }: { merchantId: number }) {
  const queryClient = useQueryClient();
  const { hasCapability } = useMyCapabilities();
  const { data: activeOverride, isLoading } = useActivePrimaryOverride(merchantId);
  const [busy, setBusy] = useState(false);

  if (!hasCapability('manage_primary_approval_override') || isLoading || !activeOverride) return null;

  const remove = async () => {
    setBusy(true);
    try {
      const { error } = await (supabaseClient.rpc as any)('clear_primary_approval_override', {
        p_merchant: merchantId,
        p_reason: 'Removed from merchant interface',
      });
      if (error) throw error;
      await refreshPrimaryState(queryClient, merchantId);
      toast.success('Manual Primary removed; automatic Primary restored');
    } catch (error) {
      toast.error(handleOperationError(error, 'Failed to remove Primary override').userMessage);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="merchant-primary-override">
      <div>
        <strong>Manual Primary</strong>
        <span>{activeOverride.submission_label || `Submission #${activeOverride.submission}`}</span>
      </div>
      <button type="button" className="sky-btn sky-btn-ghost" disabled={busy} onClick={() => void remove()}>
        {busy ? 'Removing…' : 'Remove Override'}
      </button>
    </div>
  );
}
